import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";

import Header from "../../components/Header";
import config from "../../config";
import { tokens } from "../../theme";
import { getAuthToken } from "../../utils/authStorage";
import { HPC_COLORS } from "./hpcTheme";
import { activateButtonSx, formatStatusLabel, viewProfileButtonSx } from "./hpcAdminStatusStyles";

const formatType = (value) => (value || "").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const emptyFilters = () => ({
  audience: "active", membership_types: [], q: "",
});

const disabledButtonSx = {
  "&.Mui-disabled": {
    color: "#e0e0e0",
    backgroundColor: "#525252",
    border: "2px solid #858585",
    boxShadow: "none",
  },
};

const formatReason = (value) => (value || "").replace(/_/g, " ");
const RESULT_STATUS_COLORS = { sent: "success", failed: "error", unknown: "warning" };
const responsiveButtonSx = { width: { xs: "100%", sm: "auto" }, minWidth: { sm: 220 }, py: 1 };

/** Numbered card used for each step of the page. */
const Section = ({ step, title, subtitle, colors, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 2, md: 2.5 },
      height: "100%",
      borderRadius: 2,
      backgroundColor: colors.primary[400],
      borderColor: colors.grey[700],
    }}
  >
    <Box display="flex" alignItems="flex-start" gap={1.5} mb={2.5}>
      {step && (
        <Box
          sx={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            fontWeight: 800,
            backgroundColor: HPC_COLORS.hpuOrange,
            color: "#1a1200",
          }}
        >
          {step}
        </Box>
      )}
      <Box minWidth={0}>
        <Typography variant="h5" fontWeight={700} color={colors.grey[100]} lineHeight={1.3}>{title}</Typography>
        {subtitle && <Typography variant="body2" color={colors.grey[300]} mt={0.25}>{subtitle}</Typography>}
      </Box>
    </Box>
    {children}
  </Paper>
);

/** Small labelled figure for preview and result summaries. */
const StatTile = ({ label, value, hint, color, colors }) => (
  <Box
    sx={{
      p: 1.5,
      height: "100%",
      borderRadius: 2,
      border: `1px solid ${colors.grey[700]}`,
      borderLeft: `4px solid ${color || colors.grey[500]}`,
      backgroundColor: "action.hover",
    }}
  >
    <Typography variant="h3" fontWeight={800} color={colors.grey[100]} lineHeight={1.2}>{value}</Typography>
    <Typography variant="body2" fontWeight={600} color={colors.grey[200]}>{label}</Typography>
    {hint && <Typography variant="caption" color={colors.grey[300]} display="block">{hint}</Typography>}
  </Box>
);

const ManualSmsSender = () => {
  // The global dark theme's primary.main equals the page background, so controls on this
  // page take explicit colours: [300] tokens are light in dark mode and dark in light mode.
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const accent = colors.greenAccent[300];
  const pageSx = {
    "& .MuiCheckbox-root": { color: colors.grey[300] },
    "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate": { color: accent },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accent, borderWidth: 2 },
    "& .MuiInputLabel-root.Mui-focused": { color: accent },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
      outline: `2px solid ${accent}`,
      outlineOffset: -2,
    },
  };
  const textButtonSx = {
    color: colors.blueAccent[300],
    fontWeight: 700,
    textTransform: "none",
    "&.Mui-disabled": { color: colors.grey[400] },
  };

  const [filters, setFilters] = useState(emptyFilters);
  const [criterion, setCriterion] = useState("custom");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState({});
  const [registrations, setRegistrations] = useState([]);
  const [options, setOptions] = useState({ audiences: [], membership_types: [] });
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [criterionDetails, setCriterionDetails] = useState(null);

  // Removes the preview (and its token) from the page but keeps any send result visible.
  // Frontend safeguard only: the server still accepts the token until its TTL expires.
  const discardPreview = () => {
    setPreview(null);
    setConfirmed(false);
  };

  const invalidatePreview = () => {
    discardPreview();
    setResult(null);
  };

  const updateFilters = (updates) => {
    setFilters((current) => ({ ...current, ...updates }));
    invalidatePreview();
  };

  const updateCriterion = (nextCriterion) => {
    setCriterion(nextCriterion);
    setFilters((current) => ({ ...current, membership_types: [] }));
    setSelectedIds([]);
    setSelectedMembers({});
    setCriterionDetails(null);
    invalidatePreview();
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/manual-sms/options`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        setOptions(response.data);
      } catch (error) {
        toast.error(error.response?.data?.error || "Could not load Manual SMS options");
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      setLoadingMembers(true);
      try {
        const headers = { Authorization: `Bearer ${getAuthToken()}` };
        if (criterion === "custom") {
          const params = {};
          if (filters.audience && filters.audience !== "all") params.status = filters.audience;
          if (filters.q.trim()) params.q = filters.q.trim();
          const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/registrations`, { params, headers });
          setRegistrations(response.data.registrations || []);
          setCriterionDetails(null);
        } else {
          const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/manual-sms/criteria-members`, {
            params: { criterion, q: filters.q.trim() || undefined },
            headers,
          });
          setRegistrations(response.data.members || []);
          setCriterionDetails({ ...response.data.criterion, matching_registrations: response.data.matching_registrations });
        }
      } catch (error) {
        toast.error(error.response?.data?.error || "Could not load HPC members");
      } finally {
        setLoadingMembers(false);
      }
    };
    const timer = setTimeout(loadMembers, 300);
    return () => clearTimeout(timer);
  }, [criterion, filters.audience, filters.q]);

  const requestBody = () => ({
    criterion,
    filters: Object.fromEntries(Object.entries(filters).map(([key, value]) => [key, value || null])),
    selected_membership_ids: selectedIds,
    message,
  });

  const estimate = preview?.message || { character_count: message.length, encoding: "Estimate", segments: "-", max_segments: "-" };
  const previewSendBlockReason = preview && !preview.summary.send_allowed
    ? preview.summary.valid_unique_recipients === 0
      ? "No valid SMS recipients were found for this selection."
      : preview.summary.valid_unique_recipients > preview.summary.max_recipients
        ? `This preview has ${preview.summary.valid_unique_recipients} recipients, exceeding the limit of ${preview.summary.max_recipients}.`
        : "Manual SMS is disabled on the server. Set HPC_MANUAL_SMS_ENABLED=true, then create a new preview."
    : null;

  const handlePreview = async () => {
    setPreviewing(true);
    setResult(null);
    try {
      const response = await axios.post(`${config.apiBaseUrl}/hpc/admin/manual-sms/preview`, requestBody(), {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setPreview(response.data);
      setConfirmed(false);
      if (!response.data.summary.send_allowed) {
        toast.warning("This audience cannot be sent with the current configuration.");
      }
    } catch (error) {
      setPreview(null);
      toast.error(error.response?.data?.error || "Could not preview recipients");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${config.apiBaseUrl}/hpc/admin/manual-sms/send`, {
        ...requestBody(), preview_token: preview.preview_token, confirm: true,
      }, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      setResult(response.data);
      discardPreview();
      toast.success("Manual SMS operation completed");
    } catch (error) {
      // Every rejected or failed send needs a fresh preview (400, 403, 409, 5xx, network errors).
      discardPreview();
      toast.error(error.response?.data?.error || "Could not send manual SMS");
      const status = error.response?.status;
      if (!status || status >= 500) {
        toast.warning("The send outcome is unknown and some messages may have been sent. Check before sending again.");
      }
    } finally {
      setSending(false);
    }
  };

  const updateSelectedMembers = (ids) => {
    const normalizedIds = ids.map(String);
    setSelectedIds(normalizedIds);
    setSelectedMembers((current) => {
      const next = { ...current };
      registrations.forEach((member) => {
        if (normalizedIds.includes(String(member.membership_id))) next[member.membership_id] = member;
        else delete next[member.membership_id];
      });
      return next;
    });
    invalidatePreview();
  };

  const selectAllVisible = () => updateSelectedMembers([...new Set([...selectedIds, ...registrations.map((member) => member.membership_id)])]);

  const removeSelectedMember = (membershipId) => updateSelectedMembers(selectedIds.filter((id) => id !== membershipId));

  const audienceColumns = [
    { field: "membership_id", headerName: "Membership ID", minWidth: 160, flex: 1 },
    { field: "full_name", headerName: "Name", minWidth: 180, flex: 1.2 },
    { field: "membership_type", headerName: "Package", minWidth: 160, flex: 1, valueFormatter: (value) => formatType(value) },
    { field: "status", headerName: "Status", minWidth: 130, flex: 0.8, valueFormatter: (value) => formatStatusLabel(value) },
  ];
  const resultColumns = [
    { field: "membership_id", headerName: "Membership ID", minWidth: 160, flex: 1 },
    { field: "full_name", headerName: "Name", minWidth: 180, flex: 1.2 },
    {
      field: "status", headerName: "Result", minWidth: 120, flex: 0.8,
      renderCell: ({ value }) => <Chip size="small" color={RESULT_STATUS_COLORS[value] || "default"} label={formatType(value)} />,
    },
    { field: "reason", headerName: "Reason", minWidth: 160, flex: 1, valueFormatter: (value) => formatReason(value) || "-" },
  ];

  // Display-only values derived from existing state and responses.
  const gridSx = {
    "& .MuiDataGrid-root": { border: 0 },
    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": { backgroundColor: colors.blueAccent[700] },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
  };
  const selectionSummary = selectedIds.length
    ? `${selectedIds.length} member${selectedIds.length === 1 ? "" : "s"} selected. The preview includes only selected members who still match the filters and search above.`
    : "No members selected. The preview will use everyone matching the filters above.";
  const excludedCount = preview
    ? preview.summary.excluded_missing_mobile + preview.summary.excluded_invalid_mobile + preview.summary.excluded_duplicate_mobile
    : 0;
  const sendHint = !preview
    ? null
    : !preview.summary.send_allowed
      ? "Sending is blocked for this preview."
      : !confirmed
        ? "Tick the confirmation above to enable sending."
        : "Messages are sent as soon as you press Send.";
  const resultTone = !result
    ? null
    : result.summary.sent === 0
      ? "error"
      : result.summary.failed || result.summary.unknown
        ? "warning"
        : "success";
  const resultMessage = {
    success: `All ${result?.summary.sent} messages were accepted by the SMS gateway.`,
    warning: `${result?.summary.sent} of ${result?.summary.attempted} messages were accepted. Review failed and unknown rows before sending again; unknown means the gateway did not confirm delivery.`,
    error: "No messages were accepted by the SMS gateway. Review the reasons below before trying again.",
  }[resultTone];

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }} sx={pageSx}>
      <Header title="Manual SMS" subtitle="Send a custom message to selected HPC registrations" />
      <Alert severity="warning" sx={{ mb: 2.5 }}>
        Messages are sent immediately after confirmation. Choose recipients, write the message, preview, then confirm.
      </Alert>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Section step={1} title="Recipients" subtitle="Choose an audience, then optionally pick individual members." colors={colors}>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={criterion === "custom" ? 4 : 12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>SMS Criteria</InputLabel>
                    <Select value={criterion} label="SMS Criteria" onChange={(event) => updateCriterion(event.target.value)}>
                      {options.criteria?.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                {criterion === "custom" && <>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Audience</InputLabel>
                      <Select value={filters.audience} label="Audience" onChange={(event) => updateFilters({ audience: event.target.value })}>
                        {options.audiences.map((audience) => <MenuItem key={audience} value={audience}>{audience === "all" ? "All HPC Members" : formatStatusLabel(audience) + " Members"}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Membership type</InputLabel>
                      <Select multiple value={filters.membership_types} label="Membership type" onChange={(event) => updateFilters({ membership_types: event.target.value })} renderValue={(selected) => selected.map(formatType).join(", ")}>
                        {options.membership_types.map((type) => <MenuItem key={type} value={type}>{formatType(type)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </>}
              </Grid>

              {criterionDetails && <Alert severity="info">
                <strong>{criterionDetails.label}</strong>: {criterionDetails.description}
                {criterionDetails.target_date ? ` Target date: ${criterionDetails.target_date}.` : ""}
                {` Matching registrations: ${criterionDetails.matching_registrations}.`}
              </Alert>}

              <TextField
                size="small"
                label="Search name, membership ID, or email"
                value={filters.q}
                onChange={(event) => updateFilters({ q: event.target.value })}
                helperText="Search also narrows the audience used for the preview."
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />

              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip size="small" label={`${registrations.length} shown`} />
                  <Chip
                    size="small"
                    label={`${selectedIds.length} selected`}
                    sx={selectedIds.length ? { backgroundColor: HPC_COLORS.hpuOrange, color: "#1a1200", fontWeight: 700 } : undefined}
                  />
                </Box>
                <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-end", sm: "flex-start" }}>
                  <Button size="small" sx={textButtonSx} onClick={selectAllVisible} disabled={!registrations.length}>Select all visible</Button>
                  <Button size="small" sx={textButtonSx} onClick={() => updateSelectedMembers([])} disabled={!selectedIds.length}>Clear selection</Button>
                </Stack>
              </Box>

              <Box height={{ xs: 300, md: 340 }} sx={gridSx}>
                <DataGrid rows={registrations} columns={audienceColumns} loading={loadingMembers} getRowId={(row) => row.membership_id} checkboxSelection disableRowSelectionOnClick rowSelectionModel={selectedIds} onRowSelectionModelChange={updateSelectedMembers} pageSizeOptions={[5, 10]} />
              </Box>

              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "action.hover" }}>
                <Typography variant="body2" color={colors.grey[200]} mb={selectedIds.length ? 1 : 0}>{selectionSummary}</Typography>
                {selectedIds.length > 0 && (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {selectedIds.map((membershipId) => (
                      <Chip
                        key={membershipId}
                        size="small"
                        label={selectedMembers[membershipId] ? `${selectedMembers[membershipId].full_name} (${membershipId})` : membershipId}
                        onDelete={() => removeSelectedMember(membershipId)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Section>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Section step={2} title="Message" subtitle="Write the SMS exactly as members should receive it." colors={colors}>
            <Stack spacing={2}>
              <TextField
                multiline
                minRows={8}
                label="Custom SMS message"
                placeholder="Type your message to HPU members..."
                value={message}
                onChange={(event) => { setMessage(event.target.value); invalidatePreview(); }}
                inputProps={{ maxLength: 500 }}
                helperText={`${message.length} / 500 characters`}
                FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
              />
              <Box>
                <Typography variant="caption" color={colors.grey[300]} display="block" mb={0.75}>
                  {preview ? "SMS details from the preview" : "Encoding and segment count are calculated when you preview."}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip size="small" label={`${estimate.character_count} characters`} />
                  <Chip size="small" label={estimate.encoding} />
                  <Chip size="small" label={`${estimate.segments} segments`} />
                  <Chip size="small" label={`Maximum ${estimate.max_segments} segments`} />
                </Box>
              </Box>
              <Button
                variant="contained"
                size="large"
                sx={{ ...viewProfileButtonSx, ...disabledButtonSx, py: 1.25 }}
                startIcon={<PreviewIcon />}
                onClick={handlePreview}
                disabled={previewing || sending}
                fullWidth
              >
                {previewing ? "Preparing preview..." : "Preview recipients"}
              </Button>
              <Typography variant="caption" color={colors.grey[300]} textAlign="center">
                Previewing does not send anything. Any change to recipients or the message clears the preview.
              </Typography>
            </Stack>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section step={3} title="Review and send" subtitle="Check who will receive the message before confirming." colors={colors}>
            {!preview ? (
              <Box sx={{ py: 4, px: 2, textAlign: "center", borderRadius: 2, border: `1px dashed ${colors.grey[600]}` }}>
                <PreviewIcon sx={{ fontSize: 36, color: colors.grey[400], mb: 1 }} />
                <Typography variant="body1" fontWeight={600} color={colors.grey[100]}>No preview yet</Typography>
                <Typography variant="body2" color={colors.grey[300]}>
                  Choose recipients and write a message, then select Preview recipients.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} md={3}>
                    <StatTile
                      label="Valid recipients"
                      value={preview.summary.valid_unique_recipients}
                      hint={`Limit ${preview.summary.max_recipients} per send`}
                      color={preview.summary.send_allowed ? colors.greenAccent[500] : colors.redAccent[500]}
                      colors={colors}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatTile label="Matching registrations" value={preview.summary.matched_registrations} color={colors.blueAccent[500]} colors={colors} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatTile
                      label="Excluded"
                      value={excludedCount}
                      hint={`${preview.summary.excluded_missing_mobile} missing, ${preview.summary.excluded_invalid_mobile} invalid, ${preview.summary.excluded_duplicate_mobile} duplicate`}
                      color={excludedCount ? HPC_COLORS.hpuOrange : colors.grey[500]}
                      colors={colors}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatTile
                      label="Segments per SMS"
                      value={preview.message.segments}
                      hint={`${preview.message.encoding}, maximum ${preview.message.max_segments}`}
                      color={colors.blueAccent[500]}
                      colors={colors}
                    />
                  </Grid>
                </Grid>

                <Alert severity={preview.summary.send_allowed ? "success" : "error"}>
                  {preview.summary.valid_unique_recipients} valid unique recipients from {preview.summary.matched_registrations} matching registrations. {excludedCount} excluded.
                  {` Preview is valid for ${preview.preview_ttl_seconds / 60} minutes. Changes require a new preview.`}
                </Alert>
                {previewSendBlockReason && <Alert severity="warning">{previewSendBlockReason}</Alert>}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" fontWeight={700} color={colors.grey[100]} mb={1}>Message</Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "14px 14px 14px 4px",
                        backgroundColor: "action.hover",
                        border: `1px solid ${colors.grey[700]}`,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      <Typography variant="body2" color={colors.grey[100]}>{message}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2" fontWeight={700} color={colors.grey[100]} mb={1}>
                      Recipients ({preview.recipients.length})
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" sx={{ maxHeight: 180, overflowY: "auto" }}>
                      {preview.recipients.map((recipient) => (
                        <Chip key={recipient.membership_id} size="small" label={`${recipient.full_name} (${recipient.membership_id}) - ${recipient.mobile_number_masked}`} />
                      ))}
                      {!preview.recipients.length && <Typography variant="body2" color={colors.grey[300]}>No valid recipients.</Typography>}
                    </Box>
                    {preview.excluded.length > 0 && (
                      <Box mt={1.5}>
                        <Typography variant="subtitle2" fontWeight={700} color={colors.grey[100]} mb={0.5}>
                          Excluded ({preview.excluded.length})
                        </Typography>
                        {preview.excluded.map((item) => (
                          <Typography key={`${item.membership_id}-${item.reason}`} variant="body2" color={colors.grey[300]}>
                            {item.membership_id}: {formatReason(item.reason)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    border: `1px solid ${confirmed ? colors.greenAccent[500] : colors.grey[700]}`,
                    backgroundColor: "action.hover",
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                    gap={1.5}
                  >
                    <Box>
                      <FormControlLabel control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />} label="I confirm that I want to send this SMS to the displayed recipients." />
                      {sendHint && <Typography variant="caption" color={colors.grey[300]} display="block" ml={{ xs: 0, sm: 4 }}>{sendHint}</Typography>}
                    </Box>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ ...activateButtonSx, ...disabledButtonSx, ...responsiveButtonSx }}
                      startIcon={<SendIcon />}
                      onClick={handleSend}
                      disabled={!confirmed || !preview.summary.send_allowed || sending}
                    >
                      {sending ? "Sending..." : `Send SMS to ${preview.summary.valid_unique_recipients} recipients`}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            )}
          </Section>
        </Grid>

        {result && (
          <Grid item xs={12}>
            <Section title="Send result" subtitle="Outcome reported by the SMS gateway for each recipient." colors={colors}>
              <Stack spacing={2}>
                <Alert severity={resultTone}>{resultMessage}</Alert>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={4} md>
                    <StatTile label="Attempted" value={result.summary.attempted} colors={colors} />
                  </Grid>
                  <Grid item xs={6} sm={4} md>
                    <StatTile label="Sent" value={result.summary.sent} color={colors.greenAccent[500]} colors={colors} />
                  </Grid>
                  <Grid item xs={6} sm={4} md>
                    <StatTile label="Failed" value={result.summary.failed} color={colors.redAccent[500]} colors={colors} />
                  </Grid>
                  <Grid item xs={6} sm={6} md>
                    <StatTile label="Unknown" value={result.summary.unknown} color={HPC_COLORS.hpuOrange} colors={colors} />
                  </Grid>
                  <Grid item xs={12} sm={6} md>
                    <StatTile label="Skipped" value={result.summary.skipped} hint="Excluded before sending" colors={colors} />
                  </Grid>
                </Grid>
                <Box height={300} sx={gridSx}>
                  <DataGrid rows={result.results} columns={resultColumns} getRowId={(row) => row.membership_id} hideFooter />
                </Box>
              </Stack>
            </Section>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ManualSmsSender;
